import { useEffect } from "react";
import { useSelector } from "react-redux";
import Head from "next/head";
import Link from "next/link";
import { LocalizationProvider, Localized } from "@fluent/react";

import ArrowLink from "@component/landing/arrow_link";
import CaptionedImage from "@component/landing/captioned_image";
import PageAlign from "@component/landing/page_align";
import MainCta from "@component/landing/sections/hero/main_cta";
import PricingSection from "@component/landing/sections/pricing";
import QuestionsSection from "@component/landing/sections/questions";
import { selectFeatureFlag } from "@feature/page";
import getLandingLayout from "@layout/persistent/landing";
import {
  blogUrl,
  getCreditAccountMentalModelName,
  getKikoffCreditAccountMentalModelName,
} from "@src/constants";
import analytics from "@util/analytics";
import { getLocaleCookie, l10nResources } from "@util/l10n";

import step1 from "./images/create_account_phone.svg";
import step2 from "./images/credit_service_phone.svg";
import step3 from "./images/payment_history_phone.svg";
import effect3 from "./images/piechart_account_age.svg";
import effect2 from "./images/piechart_credit_utilization.svg";
import effect1 from "./images/piechart_payment_history.svg";

import styles from "./index.module.scss";

const l10n = l10nResources({
  en: `product-hero-headline = Everything to know about the
product-howitworks-build-body = Every on-time payment you make will help you build your credit. All it takes is one click—and we’ll send you reminders to make sure.

  The first payment will be due around three weeks after you make your purchase. Then they’ll be on the same day of the month, every month.

  You can keep track of your remaining balance and credit score at any time, on your Kikoff account.
product-howitworks-build-title = 3. Build payment history every month
product-howitworks-buy-title = 2. Buy something on credit
product-howitworks-buy-body = You’ll have a \${$creditLimit} line of credit to use for Kikoff purchases. This doesn’t expire, so you can continue building your credit on your schedule.

  Choose from a variety of items from our store, starting at just $10. You’ll make your purchase with the {$kikoffCreditAccount}.
product-howitworks-signup-body = You’ll be instantly approved so you can get started that same day. We don’t do a credit check. We definitely don’t do a hard pull on your account, which could negatively impact your score.
product-howitworks-signup-title = 1. Sign up for a {$kikoffCreditAccount}
product-factor-title-first = Payment History
product-factor-body-first = This is the single most important factor in your credit score. Payment history looks at whether you’ve paid past credit bills on time.

  The {$creditAccount} encourages a long payment history, giving you maximized credit building power.

  We’ll report each of your monthly payments to the major credit bureaus. Each report builds your payment history—and your credit.
product-factor-title-second = Credit Utilization
product-factor-body-second = This is the ratio of how much credit you’ve used to how much you can use, or your credit limit. The lower this ratio, the better for your credit.

  We designed the {$creditAccount} for extremely low utilization rates. Our store items start at only $10, which means buying one on credit could run you a rate of only 2%.
product-factor-title-third = Account Age
product-factor-body-third = The older your accounts, the better. The {$creditAccount} doesn’t expire—so keeping it open will continue to increase your average account age.

  When you initially open a {$creditAccount}, you may notice your account age drops. But remember this drop is only short term! Starting a new account can bring your average age down—but keeping it open will push that age up. Opening a {$creditAccount} gives you a long term positive with age.
`,
  es: `product-hero-headline = Todo lo que hay que saber sobre el
product-howitworks-build-body = Cada pago a tiempo que haga lo ayudará a construir su crédito. Todo lo que necesita es un clic, y le enviaremos recordatorios para asegurarnos.

  El primer pago vencerá alrededor de tres semanas después de realizar su compra. Luego, estarán el mismo día del mes, todos los meses.

  Puede realizar un seguimiento de su saldo restante y puntaje de crédito en cualquier momento, en su cuenta Kikoff.
product-howitworks-build-title = 3. Crear historial de pagos cada mes
product-howitworks-buy-title = Comprar algo a credito
product-howitworks-buy-body = 2. Tendrás una línea de crédito de \${$creditLimit} para usar en la tienda Kikoff. Esto no caduca, por lo que puede continuar construyendo su crédito según su horario.

  Elija entre una variedad de artículos de nuestra tienda, desde solo $10. Realizarás tu compra con la {$kikoffCreditAccount}.
product-howitworks-signup-body = Se le aprobará instantáneamente para que pueda comenzar ese mismo día. No hacemos una verificación de crédito. Definitivamente no hacemos ningún cambio en su cuenta, lo que podría afectar negativamente su puntaje.
product-howitworks-signup-title = 1. Regístrese para obtener una {$kikoffCreditAccount}
product-factor-title-first = Historial de Pagos
product-factor-body-first = Este es el factor más importante en su puntaje de crédito. El historial de pagos analiza si pagó a tiempo las facturas de crédito anteriores.

  La {$creditAccount} fomenta un historial de pagos prolongado, lo que le brinda un poder de creación de crédito maximizado.

  Reporte cada uno de sus pagos mensuales a las principales agencias de crédito. Cada informe construye su historial de pago y su crédito.
product-factor-title-second = Utilización de Crédito
product-factor-body-second = Esta es la relación entre cuánto crédito ha usado y cuánto puede usar, o su límite de crédito. Cuanto menor sea este índice, mejor para su crédito.

  Diseñamos la {$creditAccount} para tasas de utilización extremadamente bajas. Los artículos de nuestra tienda comienzan en solo $ 10, lo que significa que comprar uno a crédito podría generarle una tasa de solo el 2%.
product-factor-title-third = Edad de la Cuenta
product-factor-body-third = Cuanto más antiguas sean sus cuentas, mejor. La {$creditAccount} no caduca, por lo que mantenerla abierta seguirá aumentando la antigüedad promedio de su cuenta.

  Cuando abre inicialmente una {$creditAccount}, es posible que observe que la antigüedad de su cuenta disminuye. ¡Pero recuerda que esta caída es solo a corto plazo! Iniciar una nueva cuenta puede reducir su edad promedio, pero mantenerla abierta aumentará esa edad. Abrir una {$creditAccount} te da un positivo a largo plazo con la edad.
`,
});

CreditAccountPage.getLayout = getLandingLayout;
export default function CreditAccountPage() {
  useEffect(() => {
    analytics.only("Criteo").convert("Hybrid Intent");
  }, []);

  return (
    <div className={styles["credit-account-page"]}>
      <Head>
        <title>Build credit with a Kikoff Credit Account | Kikoff</title>
        <meta
          name="description"
          content="With a Kikoff Credit Account you can build credit and see real improvements to your credit score.  Build good credit habits and improve your financial health"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://kikoff.com/credit-account" />
      </Head>
      <Headline />
      <HowItWorks />
      <Breakdown />
      <PricingSection />
      <div className={styles.separator} />
      <QuestionsSection showMore="expand" />
      <Learn />
    </div>
  );
}

function Headline() {
  return (
    <PageAlign>
      <LocalizationProvider l10n={l10n}>
        <section className={styles.headline}>
          <h1>
            <Localized id="product-hero-headline" />
            <br />
            <em>{getKikoffCreditAccountMentalModelName()}</em>
            <br />
            <br />
          </h1>
          <MainCta />
        </section>
      </LocalizationProvider>
    </PageAlign>
  );
}

function HowItWorks() {
  const creditLimit = 750;
  const kikoffCreditAccount = getKikoffCreditAccountMentalModelName();

  return (
    <section className={styles["how-it-works-container"]}>
      <LocalizationProvider l10n={l10n}>
        <PageAlign innerClassName={styles["how-it-works"]}>
          <header>How it works</header>
          <CaptionedImage
            body={l10n.getString("product-howitworks-signup-body")}
            className={styles.step}
            imgSide="right"
            src={step1}
            title={l10n.getString("product-howitworks-signup-title", {
              kikoffCreditAccount,
            })}
          />
          <CaptionedImage
            body={l10n.getString("product-howitworks-buy-body", {
              creditLimit,
              kikoffCreditAccount,
            })}
            className={styles.step}
            imgSide="left"
            src={step2}
            title={l10n.getString("product-howitworks-buy-title")}
          />
          <CaptionedImage
            body={l10n.getString("product-howitworks-build-body")}
            className={styles.step}
            imgSide="right"
            src={step3}
            title={l10n.getString("product-howitworks-build-title")}
          />
        </PageAlign>
      </LocalizationProvider>
    </section>
  );
}

function Breakdown() {
  const creditAccount = getCreditAccountMentalModelName();

  return (
    <PageAlign>
      <LocalizationProvider l10n={l10n}>
        <section className={styles.breakdown}>
          <header>How Kikoff helps</header>
          <div className={styles.note}>
            The {getCreditAccountMentalModelName()} addresses three key factors
            that determine your credit score: payment history, credit
            utilization, and average account age.
          </div>
          <CaptionedImage
            body={l10n.getString("product-factor-body-first", {
              creditAccount,
            })}
            className={styles.effect}
            imgSide="right"
            src={effect1}
            title={l10n.getString("product-factor-title-first")}
          />
          <CaptionedImage
            body={l10n.getString("product-factor-body-second", {
              creditAccount,
            })}
            className={styles.effect}
            imgSide="left"
            src={effect2}
            title={l10n.getString("product-factor-title-second")}
          />
          <CaptionedImage
            body={l10n.getString("product-factor-body-third", {
              creditAccount,
            })}
            className={styles.effect}
            imgSide="right"
            src={effect3}
            title={l10n.getString("product-factor-title-third")}
          />
        </section>
      </LocalizationProvider>
    </PageAlign>
  );
}

function localizedUrl(url: string) {
  const locale = getLocaleCookie();
  return locale === "es" ? `${url}?locale=es` : url;
}

function Learn() {
  const useNewArticle = useSelector(
    selectFeatureFlag("product_education_article")
  );

  const productEducationArticle = useNewArticle
    ? blogUrl(
        "/which-product-is-right-for-you-kikoffs-credit-account-kikoff-card-and-credit-builder-loan"
      )
    : blogUrl("/credit-account");

  const articles = [
    {
      title: "Credit score: the basics",
      url: blogUrl("/credit-score-the-basics"),
    },
    {
      title: "Why is my credit score important?",
      url: blogUrl("/why-is-my-credit-score-important"),
    },
    {
      title: `${getKikoffCreditAccountMentalModelName()}: Everything you need...`,
      url: productEducationArticle,
    },
    {
      title: "I have, like, no money. How do I save?",
      url: blogUrl("/no-money-save"),
    },
  ];

  return (
    <section className={styles.learn}>
      <LocalizationProvider l10n={l10n}>
        <header>Learn</header>
        <div className={styles.note}>
          Read some of our popular posts for more credit tips.
        </div>
        <div className={styles["articles-container"]}>
          <div className={styles.articles}>
            {articles.map(({ title, url }, i) => {
              return (
                <div className={styles.article} key={i}>
                  <header>{title}</header>
                  <ArrowLink href={localizedUrl(url)}>LEARN MORE</ArrowLink>
                </div>
              );
            })}
          </div>
        </div>
        <Link href={localizedUrl(blogUrl("/"))}>SEE ALL POSTS</Link>
      </LocalizationProvider>
    </section>
  );
}
